import { buffersFor, roleSides } from '@yourstore/scheduling';
import type { DayHours } from '@yourstore/domain';
import type { AppointmentSlot, Blackout, Resource, Service, Session } from '../wire';
import type { MirroredAppointment } from '../db/schema';

/**
 * The calendar's arithmetic, with no React in it.
 *
 * `CalendarView` draws; this file decides *where*. The split is the one
 * `mirror/time.ts` already makes and for the same reason: a grid is a pile of
 * small, unglamorous conversions — which local day is this instant in, how far
 * down the column does half past two sit, how tall is a fifteen-minute brow wax
 * beside a ninety-minute facial — and every one of them is a property a test can
 * state in one line as long as no renderer is in the way.
 *
 * ## The line this file does not cross
 *
 * **Nothing here decides what is free.** `packages/scheduling` is the only
 * implementation of that (dev.md rule 3) and the PWA gets those answers from
 * `GET /v1/availability`, which calls it. What this file computes is the
 * geometry of what the device already knows, plus two rules it borrows rather
 * than restates:
 *
 *  - **the gap** is `buffersFor` from `@yourstore/scheduling`, imported, never
 *    re-derived. A2 is explicit that a second answer to "how much room does this
 *    appointment take" would make the offered span, the occupied span the
 *    exclusion constraint sees, and this tail three opinions and two of them
 *    wrong. So the tail the calendar paints is the same arithmetic the database
 *    is holding;
 *  - **overlap** is half-open `[start, end)` on *occupied* spans, which is what
 *    the exclusion constraint compares. That makes a warning this file raises
 *    and a `409` the server raises agree about the boundary case of one booking
 *    ending exactly as the next begins.
 *
 * And the warning is still only a warning. An empty stretch of this grid means
 * *"nothing is booked in the copy of the book this device holds"* — never "this
 * is free" — exactly as `CalendarView`'s own note has always said.
 *
 * ## Wall clock, not epoch arithmetic
 *
 * Every vertical position is **minutes since local midnight in the tenant's
 * zone**, read out of `Intl` rather than subtracted from an epoch. The
 * difference is invisible for 363 days a year and wrong on the other two: on the
 * morning the clocks go forward, nine o'clock is eight hours after local
 * midnight rather than nine, so `(startMs - midnightMs) / 60000` draws the whole
 * day an hour high. A grid's axis is a wall clock, and this is the only way to
 * ask a wall clock what time it is.
 */

/* ------------------------------------------------------------------ */
/* Policy                                                              */
/* ------------------------------------------------------------------ */

export interface CalendarPolicy {
  /** What a drag snaps to and a keyboard arrow moves by. */
  slotGridMin: number;
  /** The house's own gap, as `buffersFor` wants it. */
  buffers: { default_buffer_before_min: number; default_buffer_after_min: number };
}

/**
 * The tenant's two scheduling numbers, with the one honest fallback.
 *
 * A `core-api` older than S1 answers `GET /v1/session` without the block, and a
 * device holding such a response is not entitled to invent a house gap — so the
 * fallback is *fifteen minutes and no gap*, which is what every tenant that has
 * never set one has. Spelled here once so no component defaults it a second way.
 */
export function calendarPolicy(session: Session | null): CalendarPolicy {
  const scheduling = session?.scheduling;
  return {
    slotGridMin: scheduling?.slot_grid_min ?? 15,
    buffers: {
      default_buffer_before_min: scheduling?.default_buffer_before_min ?? 0,
      default_buffer_after_min: scheduling?.default_buffer_after_min ?? 0,
    },
  };
}

/* ------------------------------------------------------------------ */
/* Days and weeks                                                      */
/* ------------------------------------------------------------------ */

/**
 * Monday-first, matching `DayHours.weekday` and LoveSpa's `meta.work_hours`.
 *
 * There is no `week_start` in `TenantConfig` and this is deliberate rather than
 * pending: Monday-first is the repository's one vocabulary for a weekday — the
 * hours document, `weekdayNames`, the engine's `weekdayIndex` — and a second
 * origin reachable from a config row would mean a tenant could set the calendar
 * and the hours editor to disagree about which column Sunday is.
 */
export function weekdayIndex(isoDay: string): number {
  const at = new Date(`${isoDay}T12:00:00Z`);
  return (at.getUTCDay() + 6) % 7;
}

/** The Monday of the week `isoDay` falls in. */
export function weekStartOf(isoDay: string): string {
  return addIsoDays(isoDay, -weekdayIndex(isoDay));
}

/** `count` consecutive days from `fromDay`, inclusive. */
export function daysFrom(fromDay: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addIsoDays(fromDay, i));
}

/**
 * Day arithmetic on the `YYYY-MM-DD` string itself — no zone, no DST trap.
 *
 * The same function `mirror/time.ts` exports as `addDays`, duplicated **only**
 * in the sense that a one-line UTC calendar step is not a rule: it is not
 * imported because this module must stay loadable by a test that has no
 * `messages` bundle behind it, and `mirror/time.ts` resolves a translator at
 * module load. Nothing about "which day follows this one" can drift.
 */
export function addIsoDays(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  at.setUTCDate(at.getUTCDate() + delta);
  return at.toISOString().slice(0, 10);
}

/* ------------------------------------------------------------------ */
/* Wall clock                                                          */
/* ------------------------------------------------------------------ */

export interface WallClock {
  /** `YYYY-MM-DD` in the tenant's zone. */
  day: string;
  /** Minutes since local midnight, `0 … 1439`. */
  minutes: number;
}

const ZONE_PARTS = new Map<string, Intl.DateTimeFormat>();

function zoneFormat(timezone: string): Intl.DateTimeFormat {
  let format = ZONE_PARTS.get(timezone);
  if (format === undefined) {
    // `en-CA` is a *format* and not a language here — it is the shortest way to
    // get `YYYY-MM-DD` and a zero-padded 24-hour clock out of `Intl`, which is
    // the same argument `mirror/time.ts` makes at its own `en-CA`.
    format = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    ZONE_PARTS.set(timezone, format);
  }
  return format;
}

/** Where an instant falls on a wall clock in the tenant's zone. */
export function wallClockOf(iso: string | number | Date, timezone: string): WallClock {
  const at = iso instanceof Date ? iso : new Date(iso);
  const parts = zoneFormat(timezone).formatToParts(at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00';
  // `hour12: false` renders midnight as `24` in some ICU versions, and a `24`
  // belongs to the *next* day's zero rather than to this day's end.
  const rawHour = get('hour');
  const hour = rawHour === '24' ? 0 : Number(rawHour);
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: hour * 60 + Number(get('minute')),
  };
}

/**
 * The inverse: a wall clock in the tenant's zone → an instant.
 *
 * Two passes, because a zone's offset is itself a function of the instant.
 * `mirror/time.ts`'s `zonedWallClockToIso` is the same correction written for a
 * `datetime-local` string; this one takes a day and a minute count, which is
 * what a grid actually holds, and it is the function a drop calls.
 */
export function wallClockToIso(day: string, minutes: number, timezone: string): string {
  const clamped = Math.max(0, Math.round(minutes));
  const asIfUtc = Date.parse(`${day}T00:00:00Z`) + clamped * 60_000;
  const first = zoneOffsetMs(timezone, asIfUtc);
  let instant = asIfUtc - first;
  const second = zoneOffsetMs(timezone, instant);
  if (second !== first) instant = asIfUtc - second;
  return new Date(instant).toISOString();
}

function zoneOffsetMs(timezone: string, atMs: number): number {
  const clock = wallClockOf(atMs, timezone);
  const asIfUtc = Date.parse(`${clock.day}T00:00:00Z`) + clock.minutes * 60_000;
  // Seconds are not in `zoneFormat`; every IANA offset in use is a whole
  // minute, so dropping them costs nothing and keeps one formatter.
  return asIfUtc - Math.floor(atMs / 60_000) * 60_000;
}

/** `09:30` for 570 — the grid's own rulers, in a format `Intl` does not give. */
export function clockLabel(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** `HH:MM` → minutes. The shape every `DayHours` window is written in. */
export function windowMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/* ------------------------------------------------------------------ */
/* The grid's vertical extent                                          */
/* ------------------------------------------------------------------ */

export interface GridBounds {
  /** Minutes since local midnight at the top of the grid. */
  fromMin: number;
  /** …and at the bottom. Always greater than `fromMin`. */
  toMin: number;
}

/** 8 am to 7 pm: a working day for a business that has told us nothing yet. */
export const DEFAULT_BOUNDS: GridBounds = { fromMin: 8 * 60, toMin: 19 * 60 };

/**
 * How much of the day the grid shows.
 *
 * The open windows decide it, and **everything on the book widens it**. A
 * booking outside opening hours is exactly the booking an owner needs to see —
 * she squeezed somebody in at eight on a Tuesday she is nominally shut — and a
 * grid clipped to the hours document would hide it perfectly.
 *
 * Rounded out to the hour so the ruler reads in whole hours, and never narrower
 * than four, so a tenant open for one appointment a day does not get a grid two
 * rows tall.
 */
export function gridBounds(
  windows: readonly { start: string; end: string }[],
  spans: readonly { topMin: number; endMin: number }[],
): GridBounds {
  let from = Number.POSITIVE_INFINITY;
  let to = Number.NEGATIVE_INFINITY;
  for (const w of windows) {
    from = Math.min(from, windowMinutes(w.start));
    to = Math.max(to, windowMinutes(w.end));
  }
  for (const span of spans) {
    from = Math.min(from, span.topMin);
    to = Math.max(to, span.endMin);
  }
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return DEFAULT_BOUNDS;
  from = Math.max(0, Math.floor(from / 60) * 60);
  to = Math.min(1440, Math.ceil(to / 60) * 60);
  if (to - from < 240) to = Math.min(1440, from + 240);
  return { fromMin: from, toMin: to };
}

/** The hour marks between the bounds, inclusive of the top and exclusive of the foot. */
export function hourMarks(bounds: GridBounds): number[] {
  const marks: number[] = [];
  for (let m = bounds.fromMin; m < bounds.toMin; m += 60) marks.push(m);
  return marks;
}

/* ------------------------------------------------------------------ */
/* Blocks                                                              */
/* ------------------------------------------------------------------ */

export interface PlacedBlock {
  appointment: MirroredAppointment;
  /** The local day this block is drawn on. */
  day: string;
  /** Minutes since local midnight where the sold span begins. */
  topMin: number;
  /** The sold span's height, in minutes — a 15-minute brow is 15 tall. */
  heightMin: number;
  /** The house gap after it, drawn as a lighter tail. `0` when there is none. */
  tailMin: number;
  /** Where the sold span ends. `topMin + heightMin`. */
  endMin: number;
  /** A queued `appointment.reschedule` is moving this block right now. */
  pending: boolean;
  /** Side-by-side packing within its column: `0 … columns - 1`. */
  column: number;
  columns: number;
}

/**
 * **The real span, and the room it really takes.**
 *
 * The height is the booking's own length — `docs/lovespa-discovery.md` §2.1, a
 * fifteen-minute brow wax next to a thirty-five-minute one for the guest who
 * needs thirty-five — read off the slot rather than off the catalog, because
 * since A1 the two are allowed to differ and the slot is the one that is true.
 *
 * Measured on the **wall clock** at both ends, so a booking that straddles a
 * clock change is as tall as it looks on the wall. A span that crosses local
 * midnight is clipped to the foot of its own day rather than wrapping: this grid
 * has one day per column and half a block in tomorrow's column would be a lie
 * about tomorrow.
 */
export function placeBlock(
  appointment: MirroredAppointment,
  timezone: string,
  gapAfterMin: number,
  pending: boolean,
): PlacedBlock {
  const start = wallClockOf(appointment.slot.start, timezone);
  const end = wallClockOf(appointment.slot.end, timezone);
  const endMin = end.day === start.day ? end.minutes : 1440;
  // A zero-length or inverted span still has to be visible and clickable; five
  // minutes is the shortest thing the domain lets anybody book.
  const heightMin = Math.max(5, endMin - start.minutes);
  return {
    appointment,
    day: start.day,
    topMin: start.minutes,
    heightMin,
    endMin: start.minutes + heightMin,
    tailMin: Math.max(0, gapAfterMin),
    pending,
    column: 0,
    columns: 1,
  };
}

/**
 * The gap after one booking, through `buffersFor` and through nothing else.
 *
 * A service the mirror has not got — a booking of an item retired since, or a
 * catalog that has not hydrated yet — takes the house default, which is what
 * `buffersFor` answers for an item sitting at zero anyway. Inventing nothing is
 * the alternative and it would draw a spa that keeps half an hour between
 * guests as one that keeps none.
 */
export function gapAfter(
  appointment: MirroredAppointment,
  services: readonly Service[],
  policy: CalendarPolicy,
): number {
  const service = services.find((s) => s.service_id === appointment.service_id);
  // `?? null`, not `?? 0`: a service the mirror has not got yet is one that
  // has not said, and since 0064 "not said" is the house's gap, not none.
  return buffersFor(policy.buffers, {
    buffer_before_min: service?.buffer_before_min ?? null,
    buffer_after_min: service?.buffer_after_min ?? null,
  }).after_min;
}

/**
 * Side-by-side packing for blocks that overlap in one column.
 *
 * A solo esthetician with one room never sees this; a tenant with two people in
 * one lane, or a request drawn over the booking it is asking to displace, sees
 * it immediately. Greedy by start time, which is what every calendar does and is
 * stable: the earlier block keeps the left.
 */
export function packColumns(blocks: PlacedBlock[]): PlacedBlock[] {
  const sorted = [...blocks].sort(
    (a, b) =>
      a.topMin - b.topMin ||
      a.appointment.appointment_id.localeCompare(b.appointment.appointment_id),
  );
  let cluster: PlacedBlock[] = [];
  let clusterEnd = -1;
  const out: PlacedBlock[] = [];

  const flush = (): void => {
    for (const block of cluster) block.columns = Math.max(1, ...cluster.map((b) => b.column + 1));
    out.push(...cluster);
    cluster = [];
    clusterEnd = -1;
  };

  for (const block of sorted) {
    if (block.topMin >= clusterEnd && cluster.length > 0) flush();
    const taken = new Set(cluster.filter((b) => b.endMin > block.topMin).map((b) => b.column));
    let column = 0;
    while (taken.has(column)) column += 1;
    block.column = column;
    cluster.push(block);
    clusterEnd = Math.max(clusterEnd, block.endMin);
  }
  if (cluster.length > 0) flush();
  return out;
}

/* ------------------------------------------------------------------ */
/* Lanes                                                               */
/* ------------------------------------------------------------------ */

/**
 * **The resources that deliver services**, which is what a lane is a lane of.
 *
 * Rooms and people. Equipment is a thing a treatment needs rather than a place a
 * guest goes, and a `table_pool` is one lane with many seats by construction
 * (`resources.units`) — giving either a column of its own would draw chrome
 * around a distinction nobody at the desk makes.
 *
 * A solo owner has one and therefore **no lane chrome at all**: `laneResources`
 * returning a single row is the signal `CalendarView` reads to draw one
 * unlabelled column, because a header saying "Room One" above the only room is a
 * word she has to read every morning to learn nothing.
 */
export function laneResources(resources: readonly Resource[]): Resource[] {
  return resources.filter((r) => r.active && (r.kind === 'room' || r.kind === 'person'));
}

/** More than one place a guest can be put — the condition lanes exist for. */
export function hasLanes(resources: readonly Resource[]): boolean {
  return laneResources(resources).length > 1;
}

/* ------------------------------------------------------------------ */
/* Overlap — the warning, never the gate                               */
/* ------------------------------------------------------------------ */

export interface CandidateMove {
  appointmentId: string;
  resourceId: string;
  /**
   * The lanes the block occupies besides the one being dragged (0056). A move
   * keeps them — the server keeps the room where it can — so the clash warning
   * has to look at them too, or a massage dropped onto a free therapist whose
   * room is taken would show no ghost at all.
   */
  alsoResourceIds?: readonly string[] | undefined;
  startIso: string;
  /** The length never changes on a move — this is the booking's own. */
  durationMin: number;
  serviceId: string;
}

/** Every lane a row occupies, primary first — `slotLanes` in the engine, for the mirror's shape. */
export function slotLanes(
  slot: Pick<AppointmentSlot, 'resource_id' | 'also_resource_ids'>,
): string[] {
  return [slot.resource_id, ...(slot.also_resource_ids ?? [])];
}

/** Does this row occupy this lane — as primary or as companion? */
export function occupiesLane(row: MirroredAppointment, resourceId: string): boolean {
  return slotLanes(row.slot).includes(resourceId);
}

/**
 * **What a service needs, as the sheet draws it** (spec §4.1.2): `null` for one
 * lane — every service that predates roles, and any whose links are all
 * `'any'` — or the two sides, people and rooms. This is the engine's own
 * `roleSides`, fed the mirror's `resources[]`, and deliberately not a second
 * reading of the `'any'` rule.
 */
export function serviceRoleSides(service: Service): { people: string[]; rooms: string[] } | null {
  const roled = service.resources.filter((r) => r.role !== 'any');
  if (roled.length === 0) return null;
  return roleSides(
    service.resource_ids,
    Object.fromEntries(roled.map((r) => [r.resource_id, r.role])),
  );
}

/**
 * **What this device can already see in the way.**
 *
 * The same rule `BookSheet`'s warning uses — half-open `[start, end)` against
 * the mirrored book, per resource, ignoring cancellations and no-shows — with
 * the gap included, because a drop that lands a facial in the half hour the
 * house keeps after the previous guest is a clash the exclusion constraint will
 * refuse and the owner would rather learn about while her finger is still down.
 *
 * `occupied` spans on both sides, resolved through `buffersFor`, so this
 * comparison and the one `appointment_resources` makes are the same comparison.
 *
 * It is a **warning**. `CalendarView` draws it on the ghost and still lets the
 * drop through, for `BookSheet`'s reason: the owner may know something the iPad
 * does not, the server settles it, and a `409` becomes a task with the conflict
 * in it.
 */
export function clashFor(
  candidate: CandidateMove,
  rows: readonly MirroredAppointment[],
  services: readonly Service[],
  policy: CalendarPolicy,
): MirroredAppointment | undefined {
  const own = occupiedMs(
    Date.parse(candidate.startIso),
    Date.parse(candidate.startIso) + candidate.durationMin * 60_000,
    candidate.serviceId,
    services,
    policy,
  );
  if (Number.isNaN(own.start)) return undefined;

  const lanes = [candidate.resourceId, ...(candidate.alsoResourceIds ?? [])];
  return rows.find((row) => {
    if (row.appointment_id === candidate.appointmentId) return false;
    if (!lanes.some((lane) => occupiesLane(row, lane))) return false;
    if (row.status === 'cancelled' || row.status === 'no_show') return false;
    const theirs = occupiedMs(
      Date.parse(row.slot.start),
      Date.parse(row.slot.end),
      row.service_id,
      services,
      policy,
    );
    return theirs.start < own.end && own.start < theirs.end;
  });
}

function occupiedMs(
  startMs: number,
  endMs: number,
  serviceId: string,
  services: readonly Service[],
  policy: CalendarPolicy,
): { start: number; end: number } {
  const service = services.find((s) => s.service_id === serviceId);
  const buffers = buffersFor(policy.buffers, {
    buffer_before_min: service?.buffer_before_min ?? null,
    buffer_after_min: service?.buffer_after_min ?? null,
  });
  return {
    start: startMs - buffers.before_min * 60_000,
    end: endMs + buffers.after_min * 60_000,
  };
}

/* ------------------------------------------------------------------ */
/* Moving                                                              */
/* ------------------------------------------------------------------ */

/** Snap a minute count to the house's own offer step. */
export function snapToGrid(minutes: number, gridMin: number): number {
  const step = Math.max(1, gridMin);
  return Math.round(minutes / step) * step;
}

/* ------------------------------------------------------------------ */
/* Her lengths — what a resize snaps to on a service sold several ways */
/* ------------------------------------------------------------------ */

/** One length a service is sold at, as the booking would be at it. */
export interface LengthStop {
  /** The booked span at this option: its minutes plus every attached minute. */
  minutes: number;
  optionId: string;
  name: string;
  /** The **menu's** price for the option — shown, never sent (B7). */
  priceCents: number;
}

/**
 * **The lengths a resize of this booking may land on**, shortest first — or
 * `[]` when the service is not sold at several.
 *
 * The owner's bug: a facial sold at 30/60/90/120 was booked at the 120 and
 * shortened to an hour on the calendar, and the till still said $340 because
 * the booking was still the 120. On such a service a length *is* an option, so
 * the bottom edge snaps to her lengths and the intent names the option it
 * landed on; the server re-sells the booking at it.
 *
 * The span is the option's minutes **plus the attached minutes** — a facial
 * with dermaplaning on it runs 60 + 15 at the 60, and that is what the block's
 * height is. Only live options with a length count (a dish size changes no
 * span); two options of one length collapse to one stop, the booking's own
 * option winning the tie. Fewer than two distinct lengths is a service sold
 * one way, and its resize stays on the house grid exactly as before.
 */
export function lengthStops(
  appointment: Pick<MirroredAppointment, 'service_id' | 'option_id' | 'add_ons'>,
  services: readonly Service[],
): LengthStop[] {
  const service = services.find((s) => s.service_id === appointment.service_id);
  const options = [...(service?.options ?? [])]
    .filter((o) => o.active && o.duration_min !== null)
    .sort((a, b) => a.sort_order - b.sort_order);
  const attached = (appointment.add_ons ?? []).reduce((t, a) => t + (a.duration_min ?? 0), 0);

  const byMinutes = new Map<number, LengthStop>();
  for (const option of options) {
    const minutes = (option.duration_min ?? 0) + attached;
    // The wire's own bounds on a booking's length (`ClientDurationMinutes`).
    if (minutes < 5 || minutes > 480) continue;
    const seen = byMinutes.get(minutes);
    if (seen === undefined || option.option_id === appointment.option_id) {
      byMinutes.set(minutes, {
        minutes,
        optionId: option.option_id,
        name: option.name,
        priceCents: option.price_cents,
      });
    }
  }
  if (byMinutes.size < 2) return [];
  return [...byMinutes.values()].sort((a, b) => a.minutes - b.minutes);
}

/** The stop nearest a dragged length; a tie goes to the shorter. */
export function nearestStop(minutes: number, stops: readonly LengthStop[]): LengthStop | undefined {
  let best: LengthStop | undefined;
  for (const stop of stops) {
    if (best === undefined || Math.abs(stop.minutes - minutes) < Math.abs(best.minutes - minutes)) {
      best = stop;
    }
  }
  return best;
}

/**
 * **One arrow key, one of her lengths** — the next stop longer (`+1`) or
 * shorter (`-1`) than `minutes`, held at the ends. From a custom length (a
 * remembered 105) the first press lands on the neighbouring option rather than
 * skipping one.
 */
export function stepStop(
  minutes: number,
  stops: readonly LengthStop[],
  direction: 1 | -1,
): LengthStop | undefined {
  if (direction === 1) return stops.find((s) => s.minutes > minutes) ?? stops[stops.length - 1];
  return [...stops].reverse().find((s) => s.minutes < minutes) ?? stops[0];
}

/**
 * Where a dragged block would land: a day, a snapped minute, and a resource.
 *
 * The **length never changes on a move** — `rescheduleBooking` keeps the booked
 * span and `provider-api` refuses one that does not (A1, `assertMoveKeepsLength`)
 * — so this function names a start and nothing else. It is the one thing a drag
 * is allowed to say.
 */
export function moveTarget(
  block: PlacedBlock,
  day: string,
  minutes: number,
  resourceId: string,
  timezone: string,
  policy: CalendarPolicy,
): CandidateMove {
  const snapped = Math.min(1440 - 5, Math.max(0, snapToGrid(minutes, policy.slotGridMin)));
  // The block's other lanes ride along (0056): the server keeps the room where
  // it can, so the clash warning has to look at the room too — a massage
  // dropped onto a free quarter hour whose room is taken would otherwise show
  // no ghost at all. Minus the lane being dropped into, which is the primary now.
  const also = (block.appointment.slot.also_resource_ids ?? []).filter((id) => id !== resourceId);
  return {
    appointmentId: block.appointment.appointment_id,
    resourceId,
    ...(also.length === 0 ? {} : { alsoResourceIds: also }),
    startIso: wallClockToIso(day, snapped, timezone),
    durationMin: block.heightMin,
    serviceId: block.appointment.service_id,
  };
}

/* ------------------------------------------------------------------ */
/* Hours, closures and the shading                                     */
/* ------------------------------------------------------------------ */

export interface Band {
  fromMin: number;
  toMin: number;
}

/** The open windows of one day, clipped to the grid. Empty means shut. */
export function openBands(dayHours: DayHours | undefined, bounds: GridBounds): Band[] {
  if (dayHours === undefined || dayHours.closed === true) return [];
  return (dayHours.windows ?? [])
    .map((w) => ({
      fromMin: Math.max(bounds.fromMin, windowMinutes(w.start)),
      toMin: Math.min(bounds.toMin, windowMinutes(w.end)),
    }))
    .filter((band) => band.toMin > band.fromMin);
}

/**
 * The closures crossing one day in one lane, clipped to the grid.
 *
 * A blackout with a `null` `resource_id` is the whole house shut — a holiday, a
 * trip — and lands in every lane. One naming a resource lands only in that one.
 */
export function closureBands(
  blackouts: readonly Blackout[],
  day: string,
  resourceId: string | null,
  timezone: string,
  bounds: GridBounds,
): Band[] {
  const bands: Band[] = [];
  for (const blackout of blackouts) {
    if (blackout.resource_id !== null && blackout.resource_id !== resourceId) continue;
    const start = wallClockOf(blackout.start, timezone);
    const end = wallClockOf(blackout.end, timezone);
    // Days are compared as strings, which is safe for `YYYY-MM-DD` and is how
    // the mirror's own index is keyed.
    if (end.day < day || start.day > day) continue;
    const fromMin = start.day === day ? start.minutes : 0;
    const toMin = end.day === day ? end.minutes : 1440;
    const band = {
      fromMin: Math.max(bounds.fromMin, fromMin),
      toMin: Math.min(bounds.toMin, toMin),
    };
    if (band.toMin > band.fromMin) bands.push(band);
  }
  return bands;
}

/* ------------------------------------------------------------------ */
/* What the outbox is still holding                                    */
/* ------------------------------------------------------------------ */

export interface QueuedMove {
  startIso?: string | undefined;
  resourceId?: string | undefined;
  /** S1 — a queued `appointment.duration`: the length, start held still. */
  durationMin?: number | undefined;
  /**
   * The length it is re-sold at, when the resize landed on one of her options
   * — so a queued change shows the option it will be before the server says so.
   */
  optionId?: string | undefined;
}

/**
 * **The optimistic move**, read straight off the queue.
 *
 * A reschedule has no optimistic row — an edit shows through the mirrored row it
 * edits (`outbox/entry.ts`) — so the block the owner just dragged would snap
 * back the instant React repainted, and stay back until the server answered.
 * Offline that is never. So the calendar overlays every *pending*
 * `appointment.reschedule` onto the row it targets.
 *
 * The snap-back falls out of the same fact rather than being coded: a `409`
 * moves the entry to `rejected`, `rejected` is not pending, the overlay
 * disappears, and the block is drawn at the time the server says it is at —
 * while `Tasks` shows the conflict card the API already returned. Nothing is
 * merged and nothing is reconciled; the device simply stops claiming something
 * the cloud has ruled on.
 */
export function queuedMoves(
  entries: readonly {
    kind: string;
    target: Record<string, string>;
    payload: Record<string, unknown>;
  }[],
): Map<string, QueuedMove> {
  const moves = new Map<string, QueuedMove>();
  for (const entry of entries) {
    const id = entry.target['appointment_id'];
    if (id === undefined) continue;

    if (entry.kind === 'appointment.reschedule') {
      const start = entry.payload['start'];
      if (typeof start !== 'string') continue;
      const resourceId = entry.payload['resource_id'];
      // **Merged, not replaced.** The outbox is a total order and an owner may
      // well move a booking and then stretch it; the two intents are different
      // verbs about the same row, and showing only the later one would redraw
      // the block as though she had undone the first.
      moves.set(id, {
        ...moves.get(id),
        startIso: start,
        ...(typeof resourceId === 'string' ? { resourceId } : {}),
      });
      continue;
    }

    if (entry.kind === 'appointment.duration') {
      const minutes = entry.payload['duration_min'];
      if (typeof minutes !== 'number') continue;
      const optionId = entry.payload['option_id'];
      moves.set(id, {
        ...moves.get(id),
        durationMin: minutes,
        ...(typeof optionId === 'string' ? { optionId } : {}),
      });
    }
  }
  return moves;
}

/**
 * Apply what the queue is still holding to a mirrored row.
 *
 * A move keeps the length exactly; a length change keeps the start exactly.
 * Each verb touches its own half, which is the same separation the server
 * keeps — and the reason both can be in flight at once without either of them
 * quietly undoing the other.
 */
export function withQueuedMove(row: MirroredAppointment, move: QueuedMove): MirroredAppointment {
  const startIso = move.startIso ?? row.slot.start;
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return row;
  const lengthMs =
    move.durationMin === undefined
      ? Date.parse(row.slot.end) - Date.parse(row.slot.start)
      : move.durationMin * 60_000;
  return {
    ...row,
    ...(move.optionId === undefined ? {} : { option_id: move.optionId }),
    duration_min: Math.round(lengthMs / 60_000),
    slot: {
      // The companion lanes ride along unchanged: a queued move names a new
      // start and maybe a new primary, and the server keeps the room where it
      // can. Spreading the row's own slot is what keeps them on the ghost.
      ...row.slot,
      start: startIso,
      end: new Date(start + lengthMs).toISOString(),
      resource_id: move.resourceId ?? row.slot.resource_id,
    },
  };
}

/* ------------------------------------------------------------------ */
/* What is drawn at all                                                */
/* ------------------------------------------------------------------ */

/**
 * **Cancelled is hidden; completed is muted; requested is dashed.**
 *
 * A cancelled booking is a slot that is free again, and drawing it would be the
 * calendar disagreeing with availability about the one thing they must not
 * disagree about. `no_show` is kept: the guest did not come but the hour was
 * hers and it is gone, and an owner looking back at Thursday is entitled to see
 * why it was empty. `quarantined` is kept for the same reason — it is a row
 * somebody has to deal with, not a row that never happened.
 */
export function isDrawable(row: MirroredAppointment): boolean {
  return row.status !== 'cancelled';
}
